<template>
    <div class="lms__superadmin_workspaces_edit_feedbackforms">
        <template v-if="isAddMode">
            <component-empty-state
                :item="'Key'"
                :title="'Feedback forms for the workspace can be added when workspace is created. Save workspace data first.'"
                :size="'full'"
                :submit-button-active="false"
            />
        </template>
        <template v-else>
            <component-title
                :class="'lms__superadmin_workspaces_edit_feedbackforms_title'"
                :size="'large'"
            >Feedback forms
            </component-title>
            <form-checkbox
                :class="'lms__superadmin_workspaces_edit_feedbackforms_checkboxactive'"
                :label="''"
                :name="'lms-superadmin-workspaces-edit-feedbackforms-checkboxactive'"
                :prevent="true"
                :switcher="true"
                :disabled="handleFeedbackFormsStatusRequestPending"
                :switcher-text-active="'Feedback forms enabled'"
                :switcher-text-inactive="'Feedback forms disabled'"
                :value="workspaceFeedbackFormsEnabled"
                v-on="workspaceFeedbackFormsEnabled ? { 'input': handleDisableFeedbackForms } : { 'input': handleEnableFeedbackForms }"
            >
            </form-checkbox>
            <template v-if="!workspaceFeedbackFormsEnabled">
                <component-note>Learners do not have to give feedback on the courses where required whilst feedback forms are disabled.</component-note>
            </template>
            <component-list
                ref="feedbackformslist"
                :class="[
                    'lms__superadmin_workspaces_edit_feedbackforms_list',
                    !workspaceFeedbackFormsEnabled ? 'lms__superadmin_workspaces_edit_feedbackforms_list--feedbackformsdisabled' : '',
                    workspaceFeedbackFormsKeys.length ? 'lms__superadmin_workspaces_edit_feedbackforms_list--hasresults' : ''
                ]"
                :component-table="LmsSuperadminWorkspaceEditFeedbackFormsTable"
                :component-table-name="'keys'"
                :get-items-getter="'getLmsSuperadminWorkspaceFeedbackFormsData'"
                :get-items-dispatch="workspaceId ? 'lmsSuperadminWorkspaceFeedbackFormsGet' : ''"
                :get-items-dispatch-data="{ workspaceId }"
                :item-name="['feedback form', 'feedback forms']"
                :filter-search-active="true"
                :filter-search-queryable="true"
                :filter-details-active="false"
                :filter-actions-active="true"
                :filter-actions-primary-button-text="'Create new template'"
                :filter-actions-primary-button-active="true"
                :filter-overlay="false"
                :empty-state-title="workspaceFeedbackFormsEnabled ? `Nice! \n\n Go ahead and create a new template for learners` : `Feedback forms are currently inactive. \n\n Go ahead, enable feedback forms \n then create a new template.`"
                :empty-state-button-active="workspaceFeedbackFormsEnabled"
                :empty-state-button-text="'Create new template'"
                @emit-actions-primary="handleCreateTemplate"
                @emit-remove="performFeedbackFormRemove"
                @emit-make-copy="performFeedbackFormClone"
                @emit-make-default="performFeedbackFormSetDefault"
                @emit-edit="handleEditFeedbackForm"
                @emit-empty-state-submit="handleCreateTemplate"
            ></component-list>
            <pop-notify
                ref="notifydisablefeedbackforms"
                :class="'pop__notify--remove'"
                :title-text="'Disable feedback forms?'"
                :content-text="`Disabling feedback forms means learners will not have to give feedback on the courses where required.\n\nAny learner that is yet to give feedback to complete a course will automatically complete the course.\n\nThis may take some time to update user status', however you can continue to use the application.\n\nAre you sure you want to disable feedback forms for this workspace?`"
                :cancel-button-text="'Cancel'"
                :submit-button-text="'Disable'"
                :submit-button-danger="true"
                :size="'medium'"
                :prevent-close="true"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-name="'disable-feedback-forms'"
                @emit-submit="performDisableFeedbackForms"
                @emit-close="() => popNotifyRerenderIndex++"
            />
            <pop-notify
                ref="notifyenablefeedbackforms"
                :class="'pop__notify--remove'"
                :title-text="'Enable feedback forms?'"
                :content-text="`Enabling feedback forms will require learners to give feedback on the courses where required.\n\nAre you sure you want to enable feedback forms for this workspace?`"
                :cancel-button-text="'Cancel'"
                :submit-button-text="'Enable'"
                :size="'medium'"
                :prevent-close="true"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-name="'enable-feedback-forms'"
                @emit-submit="performEnableFeedbackForms"
                @emit-close="() => popNotifyRerenderIndex++"
            />
        </template>
    </div>
</template>

<script>
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentEmptyState from '@/components/default/shared/ComponentEmptyState'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import ComponentList from '@/components/default/shared/ComponentList'
import PopNotify from '@/components/default/shared/PopNotify'
import LmsSuperadminWorkspaceEditFeedbackFormsTable from '@/components/lms/superadmin/workspace/edit/LmsSuperadminWorkspaceEditFeedbackFormsTable'
import ComponentNote from '@/components/default/shared/ComponentNote'

export default {
  name: 'lms-superadmin-workspace-edit-feedback-forms',
  components: { ComponentNote, PopNotify, ComponentList, FormCheckbox, ComponentTitle, ComponentEmptyState },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  data () {
    return {
      user: new User(),
      handleFeedbackFormsStatusRequestPending: false,
      popNotifyRerenderIndex: 0,
      popNotifyProcess: 'add',
      LmsSuperadminWorkspaceEditFeedbackFormsTable
    }
  },
  computed: {
    workspaceId () {
      return this.$route.params.id
    },
    workspaceCustomerId () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomerId
    },
    workspaceFeedbackFormsEnabled: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceFeedbackFormsEnabled
      }
    },
    workspaceFeedbackFormsKeys: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceFeedbackFormsData
      }
    },
    workspaceFeedbackForms () {
      return this.$store.getters.getLmsSuperadminWorkspaceFeedbackFormsData
    },
    feedbackFormStatus: {
      get () {
        const rawValue = this.$store.getters.getLmsSuperadminWorkspaceFeedbackFormsStatus
        return rawValue === 'active'
      }
    },
    isAddMode () {
      return this.$route.name.includes('workspace-add')
    },
    isEditMode () {
      return this.$route.name.includes('workspace-edit')
    }
  },
  methods: {
    handleCreateTemplate () {
      this.$router.push({ name: 'lms-superadmin-workspace-edit-feedback-forms-add', params: { workspaceId: this.workspaceId } })
    },
    handleEditFeedbackForm (data) {
      this.$router.push({ name: 'lms-superadmin-workspace-edit-feedback-forms-edit', params: { workspaceId: this.workspaceId, feedbackFormId: data } })
    },
    handleDisableFeedbackForms () {
      this.openDisableFeedbackFormsNotify()
    },
    handleEnableFeedbackForms () {
      this.openEnableFeedbackFormsNotify()
    },
    performDisableFeedbackForms () {
      this.$store.dispatch('lmsSuperadminWorkspaceDeactivateFeedbackForms').then(() => {
        this.closeDisableFeedbackFormsNotify()
      }).finally(() => {
        this.$eventBus.$emit('stop-disable-feedback-forms')
      })
    },
    performEnableFeedbackForms () {
      this.$store.dispatch('lmsSuperadminWorkspaceActivateFeedbackForms').then(() => {
        this.closeEnableFeedbackFormsNotify()
      }).finally(() => {
        this.$eventBus.$emit('stop-enable-feedback-forms')
      })
    },
    performFeedbackFormSetDefault (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceFeedbackFormSetDefault', { workspaceId: this.workspaceId, feedbackFormId: data }).then(() => {
        this.$refs.feedbackformslist.getItems()
      })
    },
    performFeedbackFormClone (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceFeedbackFormClone', { workspaceId: this.workspaceId, feedbackFormId: data }).then(response => {
        const feedbackFormId = response.id
        this.handleEditFeedbackForm(feedbackFormId)
      })
    },
    performFeedbackFormRemove (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceFeedbackFormRemove', { workspaceId: this.workspaceId, feedbackFormId: data }).then(() => {
        this.$refs.feedbackformslist.getItems()
      })
    },
    openDisableFeedbackFormsNotify () {
      this.$refs.notifydisablefeedbackforms.openNotify()
    },
    closeDisableFeedbackFormsNotify () {
      this.$refs.notifydisablefeedbackforms.closeNotify()
    },
    openEnableFeedbackFormsNotify () {
      this.$refs.notifyenablefeedbackforms.openNotify()
    },
    closeEnableFeedbackFormsNotify () {
      this.$refs.notifyenablefeedbackforms.closeNotify()
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddeditfeedbackforms.scss";
</style>
